import {
  Button,
  ButtonGroup,
  FieldSet,
  Form,
  Heading,
  Legend,
  Modal,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import type React from 'react';
import { useContext, useState } from 'react';
import {
  useDeleteAttachmentsModal_GetListingAliasQuery,
  useDeleteAttachmentsMutation,
} from '../../../types/GraphqlTypes';
import { inboxPagePreventMessageListScrollVariable } from '../../../lib/apollo';
import useModal from '../../../hooks/useModal';
import { AppContext } from '../../../contexts/AppContextProvider';
import { InboxPageContext } from '../../../contexts/InboxPageContextProvider';
import stringUtils from '../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../MessageAttachments';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import AttachmentCardSelector from './components/AttachmentCardSelector';
import { logApolloError } from '../../../utils/errorUtils';

type DeleteAttachmentsProps = {
  attachments: AttachmentsForMessage;
  messageId: string;
};

export type HandleSelectAttachmentToDelete = (zDocId: string, isChecked: boolean) => void;

export default function DeleteAttachments({ attachments, messageId }: DeleteAttachmentsProps) {
  const { conversationId } = useContext(InboxPageContext);
  const { isImpersonator } = useContext(AppContext);
  const { clearModal } = useModal();
  const { enqueueToast } = useToast();

  const { data: listingAliasData } = useDeleteAttachmentsModal_GetListingAliasQuery({
    variables: {
      conversationId: conversationId!,
    },
    fetchPolicy: 'cache-only',
  });

  const [checkedAttachmentsZDocIds, setCheckedAttachmentsZDocIds] = useState<Array<string>>([]);
  const isDeleteButtonDisabled = checkedAttachmentsZDocIds.length === 0 || isImpersonator;
  const legendText = `Select ${stringUtils.pluralize('attachment', attachments.length)} to delete`;
  const { listingAlias } = listingAliasData?.ZRM_getConversationById || {};

  const [deleteAttachmentsMutation] = useDeleteAttachmentsMutation();

  const handleClose = () => clearModal();

  const handleSelectAttachmentToDelete: HandleSelectAttachmentToDelete = (zDocId, isChecked) => {
    if (isChecked) {
      setCheckedAttachmentsZDocIds([...checkedAttachmentsZDocIds, zDocId]);
    } else {
      const filteredAttachmentsZDocIds = checkedAttachmentsZDocIds.filter(
        (checkedAttachment) => checkedAttachment !== zDocId,
      );
      setCheckedAttachmentsZDocIds(filteredAttachmentsZDocIds);
    }
  };

  const handleDeleteAttachments = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const attachmentsToBeDeleted = checkedAttachmentsZDocIds.map((zDocId) => {
      return {
        messageId,
        zDocId,
      };
    });

    deleteAttachmentsMutation({
      variables: {
        conversationId: conversationId!,
        listingAlias: listingAlias!,
        attachmentsToBeDeleted,
      },
      onCompleted() {
        enqueueToast(<Toast tone="success">Deleted</Toast>);
      },
      update(cache) {
        checkedAttachmentsZDocIds.forEach((zDocId) => {
          return cache.modify({
            id: cache.identify({
              __typename: 'ZRM_Attachment',
              zDocId: zDocId,
            }),
            broadcast: false,
            fields: {
              isAttachmentDeleted() {
                return true;
              },
            },
          });
        });

        inboxPagePreventMessageListScrollVariable(true);
        clearModal();
      },
      onError(error) {
        logApolloError(error, {
          details: 'DeleteAttachmentsModal - Error deleting attachments',
          conversationId,
          listingAlias,
          attachmentsToBeDeleted,
        });

        enqueueToast(
          <Toast tone="critical">Sorry, there was an error deleting one or more attachments. Please try again.</Toast>,
        );

        clearModal();
      },
    });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading textStyle="heading-xs" level={2}>
          Delete?
        </Heading>
      }
      body={
        <Form onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
          <FieldSet legend={<Legend size="lg">{legendText}</Legend>} css={{ minWidth: '100%' }}>
            {attachments.map(({ fileName, fileType, isAttachmentExpired, isAttachmentDeleted, previewUrl, zDocId }) => {
              if (isAttachmentExpired || isAttachmentDeleted) {
                return;
              }

              const key = `${fileName}-${zDocId}`;

              return (
                <AttachmentCardSelector
                  key={key}
                  fileName={fileName || ''}
                  fileType={fileType || ''}
                  imageSrc={previewUrl || ''}
                  onSelect={handleSelectAttachmentToDelete}
                  zDocId={zDocId || ''}
                />
              );
            })}
          </FieldSet>
        </Form>
      }
      footer={
        <ButtonGroup aria-label="form actions">
          <TextButton onClick={handleClose}>Cancel</TextButton>
          <TooltipWrapper wrapInTooltip={isImpersonator}>
            <Button
              disabled={isDeleteButtonDisabled}
              emphasis="filled"
              onClick={handleDeleteAttachments}
              tone="brand"
              type="submit"
            >
              Delete
            </Button>
          </TooltipWrapper>
        </ButtonGroup>
      }
    />
  );
}
